/**
 * Learning from failures, the "just culture" way (core review.ts, FRAMEWORK S9):
 * - TipBody: a PRIVATE heads-up for the last person who logged the related check: plain headline, one skill tip (quoted
 *   from the sourced how-to), one habit tip, "Got it". Calm tone: no red, no warning icon, nothing about fault.
 * - LearningSection (Team tab, managers): PATTERNS and system fixes only. A single event never shows here; a person is
 *   named only at the server's pattern threshold. No leaderboard, no per-person score, no ranking.
 */
import { useState } from "react";
import { lessonTitle, type CauseId, type SystemFix } from "@logwave/core";
import type { LearningResponse, TipView } from "../api.ts";
import { Icon } from "./Icons.tsx";
import { Button, Card, LinkButton, Pill, Section, useToast } from "./ui.tsx";
import { useApi, useSession } from "../session.tsx";
import { isoToLocalTime } from "../lib/time.ts";

/** "Yesterday · " or "Sep 23 · " when the tip isn't from today (the headline carries clock times only). */
function dayWord(iso: string, tz: string): string {
  const d = (x: Date) => x.toLocaleDateString("en-CA", { timeZone: tz });
  const then = d(new Date(iso)), today = d(new Date()), yest = d(new Date(Date.now() - 86400000));
  if (then === today) return "";
  if (then === yest) return "Yesterday · ";
  return `${new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: tz })} · `;
}


/** The tip itself: headline, skill, habit. Shared by the Today card and the Profile history. */
export function TipBody({ tip, onAck, compact }: { tip: Omit<TipView, "kind" | "locationId">; onAck?: (t: TipView) => void; compact?: boolean }) {
  const api = useApi();
  const s = useSession();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  async function ack() {
    setBusy(true);
    try { const t = await api.ackTip(s.org.id, tip.id); onAck?.(t); toast.show("Thanks. Tip saved under Me."); }
    catch (x) { toast.show(x instanceof Error ? x.message : "Couldn't save. Try again.", { icon: "alert" }); }
    finally { setBusy(false); }
  }
  return (
    <div className="tip">
      <p className={compact ? "tip-head-sm" : "fcard-lead tip-head"}>{tip.headline}</p>
      {!compact && <p className="caption tip-why">{dayWord(tip.at, s.location.timezone)}It's been taken care of. For next time:</p>}
      {tip.habitTip && (
        <div className="tip-row">
          <span className="tip-label">Try this</span>
          <p>{tip.habitTip}</p>
        </div>
      )}
      {tip.skillTip && (
        <div className="tip-row">
          <span className="tip-label">The rule</span>
          <p>{tip.skillTip.text}<span className="caption tip-src"> {tip.skillTip.source}</span></p>
        </div>
      )}
      {(onAck || tip.lessonId) && (
        <div className="btn-row">
          {onAck && !tip.ackedAt && <Button icon="check" variant="secondary" busy={busy} onClick={() => void ack()}>I've got it</Button>}
          {tip.lessonId && <LinkButton small variant="quiet" icon="learn" to={`/learn/lesson/${tip.lessonId}`}>{`Open the lesson: ${lessonTitle(tip.lessonId) ?? "lesson"}`}</LinkButton>}
        </div>
      )}
    </div>
  );
}

/** "Your tips": the person's own history, newest first. */
export function TipsHistory({ tips, thanks }: { tips: TipView[]; thanks: TipView[] }) {
  const s = useSession();
  const tz = s.location.timezone;
  const all = [...tips, ...thanks].sort((a, b) => b.at.localeCompare(a.at));
  if (!all.length) return <Card flat><p className="muted">No tips yet. When something runs out or reads off after a check you logged, you'll get a private heads-up here. Only you see these.</p></Card>;
  return (
    <ul className="tips-list">
      {all.map((t) => (
        <li key={t.id} className="tips-item">
          <span className="tips-when caption">{new Date(t.at).toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: tz })} · {isoToLocalTime(t.at, tz)}</span>
          {t.kind === "thanks" ? <p className="row"><Icon name="spark" size={18} /><span>{t.headline}</span></p> : <TipBody tip={t} compact />}
          {t.kind === "tip" && (t.ackedAt ? <Pill tone="go" icon="check">Got it</Pill> : <Pill tone="info">New</Pill>)}
        </li>
      ))}
    </ul>
  );
}

const CAUSE_LINE: Record<CauseId, string> = {
  supply_out: "The stock ran out, so there was nothing to refill with.",
  no_recent_check: "No check was logged before it happened.",
  new_or_untrained: "The related lesson wasn't done yet.",
  changed_after_check: "Checks looked fine; it ran out between checks.",
  check_may_be_inaccurate: "It happened soon after a check that looked fine.",
  check_interval_too_long: "The time between checks is long for this.",
};

function FixButton({ fix }: { fix: SystemFix }) {
  const s = useSession();
  const admin = s.can("admin");
  if (fix.kind === "raise_par" || fix.kind === "reorder")
    return admin ? <LinkButton small variant="secondary" icon="box" to="/settings#supplies">{fix.label}</LinkButton> : <span className="caption">{fix.label}: ask the owner (Settings, Supplies).</span>;
  if (fix.kind === "switch_level")
    return admin ? <LinkButton small variant="secondary" icon="clock" to="/settings#level">{fix.label}</LinkButton> : <span className="caption">{fix.label}: ask the owner (Settings, Logging level).</span>;
  return <LinkButton small variant="secondary" icon="learn" to={fix.lessonId ? `/learn/lesson/${fix.lessonId}` : "/learn"}>{fix.label}</LinkButton>;
}

export function LearningSection({ data }: { data: LearningResponse | null }) {
  if (!data) return null;
  const span = data.windowDays === 7 ? "this week" : `the last ${data.windowDays} days`;
  return (
    <Section title="Learning" kicker={`Patterns from ${span}, never single events`}>
      <div className="stack-sm">
        {data.patterns.length === 0 ? (
          <Card flat><p className="muted">No patterns yet. A single slip stays private between the app and the person who logged it; if the same thing keeps happening, you'll see it here with a fix.</p></Card>
        ) : data.patterns.map((p) => (
          <Card key={p.key} className="learn-card" edge="info">
            <p className="learn-title">{p.title}</p>
            {p.cause && <p className="caption">Most likely: {CAUSE_LINE[p.cause]}</p>}
            {p.fixes.length > 0 && <><p className="small">Consider:</p><div className="btn-row">{p.fixes.map((f) => <FixButton key={`${f.kind}-${f.supplyKey ?? f.level ?? f.lessonId}`} fix={f} />)}</div></>}
          </Card>
        ))}
        {data.people.map((p) => (
          <Card key={`${p.employeeId}-${p.key}`} flat className="learn-card">
            <p><strong>{p.name.split(" ")[0]}</strong> · {p.title.charAt(0).toLowerCase() + p.title.slice(1)}. {p.name.split(" ")[0]} has had a private tip each time; a lesson refresher together might help.</p>
            <div className="btn-row">
              {p.lessonId && <LinkButton small variant="secondary" icon="learn" to={`/learn/lesson/${p.lessonId}`}>{`Review: ${lessonTitle(p.lessonId) ?? "lesson"}`}</LinkButton>}
              <LinkButton small variant="quiet" icon="chevron" iconEnd to={`/people/${p.employeeId}`}>{`Open ${p.name.split(" ")[0]}'s file`}</LinkButton>
            </div>
          </Card>
        ))}
        <p className="caption">{data.tipsSent} private {data.tipsSent === 1 ? "tip" : "tips"} and {data.thanksSent} thank-{data.thanksSent === 1 ? "you" : "yous"} sent in {span}. Names appear only after {data.personThreshold} of the same kind for one person.</p>
      </div>
    </Section>
  );
}
